import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Container, Spinner } from 'react-bootstrap';
import { trackRedirectTriggered } from '../lib/analytics';

const STRAPI_URL = process.env.REACT_APP_STRAPI_URL || 'http://localhost:1337';

interface RedirectData {
    sourcePath: string;
    destinationUrl: string;
    statusCode: number;
    isWildcard: boolean;
}

/**
 * RedirectHandler component.
 * Checks Strapi for a redirect matching the current path.
 * If found, redirects to the destination URL.
 * If not found, renders the NotFound page.
 */
export default function RedirectHandler({ notFoundComponent }: { notFoundComponent: React.ReactNode }) {
    const location = useLocation();
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        const checkRedirect = async () => {
            try {
                const path = location.pathname;
                const response = await fetch(`${STRAPI_URL}/api/redirects/resolve?path=${encodeURIComponent(path)}`);

                if (response.ok) {
                    const json = await response.json();
                    const redirect: RedirectData = json.data;

                    if (redirect && redirect.destinationUrl) {
                        // Track the redirect before navigating
                        trackRedirectTriggered(
                            redirect.sourcePath,
                            redirect.destinationUrl,
                            redirect.statusCode
                        );

                        // Perform the redirect
                        // Note: window.location.replace simulates 301/302 behavior for the browser
                        // The actual HTTP status code is only relevant for server-side redirects (SEO)
                        window.location.replace(redirect.destinationUrl);
                        return;
                    }
                }

                // No redirect found or error - show 404
                setNotFound(true);
            } catch (error) {
                console.error('[RedirectHandler] Error checking redirect:', error);
                setNotFound(true);
            } finally {
                setLoading(false);
            }
        };

        checkRedirect();
    }, [location.pathname]);

    if (loading) {
        return (
            <Container className="mt-5 text-center">
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
            </Container>
        );
    }

    if (notFound) {
        return <>{notFoundComponent}</>;
    }

    // Redirecting...
    return (
        <Container className="mt-5 text-center">
            <Spinner animation="border" role="status">
                <span className="visually-hidden">Redirecting...</span>
            </Spinner>
            <div className="mt-2 text-muted">Redirecting...</div>
        </Container>
    );
}
